import React, { useMemo, useCallback } from 'react';
import { CandidatesState, MatchScore } from './types';
import { TCandidate } from './types';
import { Box, CircularProgress, IconButton, TableCell } from '@mui/material';
import { classes } from '../Components/GenericTable/styles';
import { AccessTimeFilled, Close, Check, DoneAll, LinkedIn, Visibility } from '@mui/icons-material';
import { theme } from '../../ThemeContext/ThemeObject';
import PoolList from '../CandidateSourcing/PoolList';

interface CandidatesTableRowsProps {
  column: CandidatesState['candidatesTableState']['columns'][number];
  item: TCandidate;
  tableHovered: boolean;
  onExpand?: () => void;
  onRevealContacts?: (candidate: TCandidate) => void;
  expandedPools?: Set<number>;
  onViewPool?: (poolId: number) => void;
  onTogglePoolsExpansion?: (candidateGroupId: number) => void;
}

const CandidatesTableRows = ({
  column,
  item,
  tableHovered,
  onExpand,
  onRevealContacts,
  expandedPools,
  onViewPool,
  onTogglePoolsExpansion
}: CandidatesTableRowsProps) => {
  const showAllPools = expandedPools?.has(item.candidate_group.id) || false;

  const contactsRevealed = item.candidate_group.contacts_revealed;
  const showEmails = contactsRevealed || item.candidate_group.emails.length > 0;
  const showPhones = contactsRevealed || item.candidate_group.phones.length > 0;

  const logoStyle = useMemo(
    () => ({
      width: '25px',
      height: '25px',
      backgroundImage: "url('/images/st-logo.png')",
      backgroundSize: '100% 100%'
    }),
    []
  );

  const Link = useCallback(
    ({ content }: { content: string }) => (
      <a
        href={`/admin/candidate_sourcing/candidate_groups/${item.candidate_group.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {content}
      </a>
    ),
    [item.candidate_group.id]
  );

  const sourceIcon = useCallback(
    (source: string) => {
      switch (source) {
        case 'linkedin':
          return <LinkedIn color="primary" />;
        default:
          return <div style={logoStyle} />;
      }
    },
    [logoStyle]
  );

  const sourceDisplay = useCallback(
    (candidate: { source: string; url: string }) => {
      return (
        <Box>
          <a href={candidate.url} target="_blank" rel="noopener noreferrer">
            {sourceIcon(candidate.source)}
          </a>
        </Box>
      );
    },
    [sourceIcon]
  );

  const matchingConditionIndicator = useCallback(
    (conditionId: number) => {
      const matchingCondition = item.matching_conditions.find((cond) => cond.id === conditionId);
      if (
        !matchingCondition ||
        matchingCondition.matched_score === null ||
        matchingCondition.matched_score === undefined
      ) {
        return <AccessTimeFilled color="disabled" />;
      }

      switch (matchingCondition.matched_score) {
        case MatchScore.EXCELLENT:
          return <DoneAll sx={{ color: theme.palette.success.main }} />;
        case MatchScore.GOOD:
          return <Check sx={{ color: theme.palette.secondary.yellow }} />;
        default:
          return <Close sx={{ color: theme.palette.error.main }} />;
      }
    },
    [item.matching_conditions]
  );

  const handleExpand = useCallback((event: React.MouseEvent<HTMLTableCellElement>) => {
      event.stopPropagation();
      onExpand && onExpand();
    },
    [onExpand]
  );

  const matchingPercentStyle = useMemo(
    () => ({ color: item.matching_percent_color }),
    [item.matching_percent_color]
  );

  if (!column.enabled) return null;

  if (column.id.startsWith('condition_')) {
    const conditionId = parseInt(column.id.split('_')[1]);
    return (
      <TableCell
        key={item.id + column.id}
        onClick={handleExpand}
        sx={{
          cursor: onExpand ? 'help' : 'default'
        }}
      >
        {matchingConditionIndicator(conditionId)}
      </TableCell>
    );
  }

  switch (column.id) {
    case 'id':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            ...classes.sticky,
            ...classes.itemTitleTableCell,
            left: '58px',
            ...(tableHovered && classes.scrollShadowVisible),
            width: '100px',
            minWidth: '120px'
          }}
        >
          <Link content={item.id.toString()} />
        </TableCell>
      );
    case 'name':
      return (
        <TableCell key={item.id + column.id}>
          <Link content={item.candidate_group.name} />
        </TableCell>
      );
    case 'sources':
      return (
        <TableCell key={item.id + column.id}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            {item.candidate_group.candidates.map((candidate) => sourceDisplay(candidate))}
          </Box>
        </TableCell>
      );
    case 'matching_percent':
      return (
        <TableCell
          key={item.id + column.id}
          sx={{
            cursor: onExpand ? 'help' : 'default'
          }}
          onClick={handleExpand}
        >
          <span style={matchingPercentStyle}>
            {item.matching_percent !== null && item.matching_percent !== undefined
              ? `${item.matching_percent}%`
              : 'Calculating...'}
          </span>
        </TableCell>
      );
    case 'emails':
      return (
        <TableCell key={item.id + column.id}>
          {!showEmails && !contactsRevealed && !item.revealing_contacts && (
            <IconButton onClick={() => onRevealContacts && onRevealContacts(item)}>
              <Visibility />
            </IconButton>
          )}
          {showEmails && <span>{item.candidate_group.emails.join(', ')}</span>}
          {item.revealing_contacts && <CircularProgress size={20} />}
        </TableCell>
      );
    case 'phones':
      return (
        <TableCell key={item.id + column.id}>
          {!showPhones && !contactsRevealed && !item.revealing_contacts && (
            <IconButton onClick={() => onRevealContacts && onRevealContacts(item)}>
              <Visibility />
            </IconButton>
          )}
          {showPhones && <span>{item.candidate_group.phones.join(', ')}</span>}
          {item.revealing_contacts && <CircularProgress size={20} />}
        </TableCell>
      );
    case 'pools':
      return (
        <TableCell sx={{ maxWidth: '15vw' }} key={item.id + column.id}>
          <PoolList
            candidateGroup={item.candidate_group}
            onViewPool={onViewPool}
            showAllPools={showAllPools}
            onTogglePoolsExpansion={onTogglePoolsExpansion}
            showItemsThreshold={3}
          />
        </TableCell>
      );
    default:
      return <TableCell></TableCell>;
  }
};

export default CandidatesTableRows;
